import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { fmtIDR, today } from "../lib/format";
import { Modal, Field, Btn, Badge, inputCls } from "./common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { FileSignature, Lock } from "lucide-react";

export function terbilang(n) {
  n = Math.floor(Math.abs(n || 0));
  if (n === 0) return "Nol Rupiah";
  const sat = ["", "Satu", "Dua", "Tiga", "Empat", "Lima", "Enam", "Tujuh", "Delapan", "Sembilan", "Sepuluh", "Sebelas"];
  const t = (x) => {
    if (x < 12) return sat[x];
    if (x < 20) return t(x - 10) + " Belas";
    if (x < 100) return t(Math.floor(x / 10)) + " Puluh " + t(x % 10);
    if (x < 200) return "Seratus " + t(x - 100);
    if (x < 1000) return t(Math.floor(x / 100)) + " Ratus " + t(x % 100);
    if (x < 2000) return "Seribu " + t(x - 1000);
    if (x < 1e6) return t(Math.floor(x / 1e3)) + " Ribu " + t(x % 1e3);
    if (x < 1e9) return t(Math.floor(x / 1e6)) + " Juta " + t(x % 1e6);
    if (x < 1e12) return t(Math.floor(x / 1e9)) + " Miliar " + t(x % 1e9);
    return t(Math.floor(x / 1e12)) + " Triliun " + t(x % 1e12);
  };
  return (t(n) + " Rupiah").replace(/\s+/g, " ").trim();
}

const Terbilang = ({ value, testid }) => (
  <div className="text-[10px] font-mono text-cyan-300/90 italic mt-0.5" data-testid={testid}>{terbilang(value)}</div>
);

export default function BookingFormModal({ lead, onClose, onDone }) {
  const { projects } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    location_id: "", block_id: "", unit_id: "",
    nama_pemohon: lead.name || "", no_hp_pemohon: lead.phone || "", ttl_pemohon: "",
    jenis_usaha: "", pekerjaan_pemohon: "", penghasilan_pemohon: 0, nik_pemohon: "", alamat_pemohon: "",
    nama_pasangan: "", ttl_pasangan: "", pekerjaan_pasangan: "", alamat_pasangan: "",
    payment_method: "kpr", besaran_dp: 0, dp_dibayar: 0, discount: 0, promo_dp: 0, addon_ids: [], booking_fee_gratis: false, dp_gratis: false, menikah: false,
    cabang_bank: "", ttd_cabang_bank: "", tempat_tanda_tangan: "",
  });
  const [unit, setUnit] = useState(null);
  const [unitsInBlock, setUnitsInBlock] = useState([]);
  const [kprManual, setKprManual] = useState(null);
  const [addons, setAddons] = useState([]);

  useEffect(() => {
    api.get("/settings/addons").then((r) => setAddons(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/settings/bank-branches").then((r) => {
      setBranches(r.data.branches);
      if (r.data.branches.length) setF((prev) => ({ ...prev, cabang_bank: prev.cabang_bank || r.data.branches[0], ttd_cabang_bank: prev.ttd_cabang_bank || r.data.branches[0] }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (f.location_id) api.get(`/locations/${f.location_id}/blocks`).then((r) => setBlocks(r.data)).catch(() => {});
    else setBlocks([]);
  }, [f.location_id]);

  useEffect(() => {
    if (f.block_id) {
      api.get(`/sales/units?project_id=${f.location_id}`).then((r) => {
        setUnitsInBlock(r.data.filter((u) => u.block_id === f.block_id && ["tersedia", "dibangun"].includes(u.status)));
      }).catch(() => {});
    } else setUnitsInBlock([]);
  }, [f.block_id, f.location_id]);

  useEffect(() => {
    const u = unitsInBlock.find((x) => x.id === f.unit_id);
    setUnit(u || null);
    setKprManual(null);
    if (u) setF((prev) => ({ ...prev, besaran_dp: prev.besaran_dp || Math.round(u.price * 0.2) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.unit_id, unitsInBlock]);

  const harga = unit?.price || 0;
  const addonTotal = addons.filter((a) => f.addon_ids.includes(a.id)).reduce((s, a) => s + (a.price || 0), 0);
  const hargaEff = Math.max(harga - (f.discount || 0) + addonTotal, 0);
  const kekurangan = Math.max(f.besaran_dp - (f.promo_dp || 0) - f.dp_dibayar, 0);
  const nilaiKpr = f.payment_method === "kpr" ? Math.max(hargaEff - f.besaran_dp, 0) : 0;
  const nilaiKprFinal = f.payment_method === "kpr" && kprManual !== null && kprManual !== "" ? Math.max(+kprManual || 0, 0) : nilaiKpr;
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/crm/leads/${lead.id}/booking-form`, {
        unit_id: f.unit_id, nama_pemohon: f.nama_pemohon, no_hp_pemohon: f.no_hp_pemohon,
        ttl_pemohon: f.ttl_pemohon, jenis_usaha: f.jenis_usaha, pekerjaan_pemohon: f.pekerjaan_pemohon, penghasilan_pemohon: +f.penghasilan_pemohon || 0,
        nik_pemohon: f.nik_pemohon, alamat_pemohon: f.alamat_pemohon,
        nama_pasangan: f.nama_pasangan, ttl_pasangan: f.ttl_pasangan,
        pekerjaan_pasangan: f.pekerjaan_pasangan, alamat_pasangan: f.alamat_pasangan,
        payment_method: f.payment_method, besaran_dp: +f.besaran_dp || 0, dp_dibayar: +f.dp_dibayar || 0, discount: +f.discount || 0,
        nilai_kpr: f.payment_method === "kpr" ? nilaiKprFinal : null,
        promo_dp: +f.promo_dp || 0, addon_ids: f.addon_ids,
        booking_fee_gratis: !!f.booking_fee_gratis, dp_gratis: !!f.dp_gratis, menikah: !!f.menikah,
        cabang_bank: f.cabang_bank, ttd_cabang_bank: f.ttd_cabang_bank, tempat_tanda_tangan: f.tempat_tanda_tangan,
      });
      toast.success("Formulir booking tersimpan — unit berstatus Booking & lead masuk fase Booking");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  const ready = f.unit_id && f.nama_pemohon && f.no_hp_pemohon && f.ttl_pemohon && f.nik_pemohon && f.alamat_pemohon && f.cabang_bank && f.ttd_cabang_bank && f.tempat_tanda_tangan;

  const RO = ({ label, value, terbilangVal, testid }) => (
    <div className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1"><Lock className="w-2.5 h-2.5" />{label}</div>
      <div className="text-sm text-slate-200 mt-0.5">{value}</div>
      {terbilangVal !== undefined && <Terbilang value={terbilangVal} testid={`${testid}-terbilang`} />}
    </div>
  );

  return (
    <Modal open={true} onClose={onClose} title={`Formulir Booking Konsumen — ${lead.name}`} wide testid="booking-form-modal">
      <div className="space-y-5">
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2 text-xs text-amber-300" data-testid="booking-gate-notice">
          Formulir ini wajib dilengkapi untuk memindahkan lead ke fase Booking. Data menjadi sumber utama Modul Dokumen & Penjualan.
        </div>

        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-2">A. Data Pribadi & Pekerjaan Pemohon</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nama Pemohon *"><input className={inputCls} value={f.nama_pemohon} onChange={(e) => set("nama_pemohon", e.target.value)} data-testid="bf-nama" /></Field>
            <Field label="No HP Pemohon *"><input className={inputCls} value={f.no_hp_pemohon} onChange={(e) => set("no_hp_pemohon", e.target.value)} data-testid="bf-nohp" /></Field>
            <Field label="Tempat/Tgl Lahir Pemohon *"><input className={inputCls} placeholder="Stabat, 17-08-1990" value={f.ttl_pemohon} onChange={(e) => set("ttl_pemohon", e.target.value)} data-testid="bf-ttl" /></Field>
            <Field label="NIK Pemohon *"><input className={inputCls} value={f.nik_pemohon} onChange={(e) => set("nik_pemohon", e.target.value)} data-testid="bf-nik" /></Field>
            <Field label="Penghasilan Pemohon (Rp)">
              <input type="number" className={inputCls} value={f.penghasilan_pemohon || ""} onChange={(e) => set("penghasilan_pemohon", +e.target.value)} data-testid="bf-penghasilan" />
              <Terbilang value={f.penghasilan_pemohon} testid="bf-penghasilan-terbilang" />
            </Field>
            <Field label="Jenis Usaha (opsional, wiraswasta)"><input className={inputCls} value={f.jenis_usaha} onChange={(e) => set("jenis_usaha", e.target.value)} data-testid="bf-jenis-usaha" /></Field>
            <Field label="Pekerjaan Pemohon"><input className={inputCls} placeholder="Pegawai Swasta / Wiraswasta" value={f.pekerjaan_pemohon} onChange={(e) => set("pekerjaan_pemohon", e.target.value)} data-testid="bf-pekerjaan" /></Field>
          </div>
          <Field label="Alamat Pemohon *"><textarea className={`${inputCls} mt-2`} rows={2} value={f.alamat_pemohon} onChange={(e) => set("alamat_pemohon", e.target.value)} data-testid="bf-alamat" /></Field>
        </div>

        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-2">B. Data Pasangan (Opsional)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nama Pasangan"><input className={inputCls} value={f.nama_pasangan} onChange={(e) => set("nama_pasangan", e.target.value)} data-testid="bf-nama-pasangan" /></Field>
            <Field label="Tempat/Tgl Lahir Pasangan"><input className={inputCls} value={f.ttl_pasangan} onChange={(e) => set("ttl_pasangan", e.target.value)} data-testid="bf-ttl-pasangan" /></Field>
            <Field label="Pekerjaan Pasangan"><input className={inputCls} value={f.pekerjaan_pasangan} onChange={(e) => set("pekerjaan_pasangan", e.target.value)} data-testid="bf-pekerjaan-pasangan" /></Field>
            <Field label="Alamat Pasangan"><input className={inputCls} value={f.alamat_pasangan} onChange={(e) => set("alamat_pasangan", e.target.value)} data-testid="bf-alamat-pasangan" /></Field>
          </div>
        </div>

        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-2">C. Unit Rumah & Finansial (Auto-Fetch)</div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <Field label="Lokasi Perumahan *">
              <select className={inputCls} value={f.location_id} onChange={(e) => setF({ ...f, location_id: e.target.value, block_id: "", unit_id: "" })} data-testid="bf-location-select">
                <option value="">Pilih lokasi...</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Blok *">
              <select className={inputCls} value={f.block_id} onChange={(e) => setF({ ...f, block_id: e.target.value, unit_id: "" })} data-testid="bf-block-select">
                <option value="">Pilih blok...</option>
                {blocks.map((b) => <option key={b.id} value={b.id}>Blok {b.name}</option>)}
              </select>
            </Field>
            <Field label="Unit (tersedia / dibangun) *">
              <select className={inputCls} value={f.unit_id} onChange={(e) => set("unit_id", e.target.value)} data-testid="bf-unit-select">
                <option value="">Pilih unit...</option>
                {unitsInBlock.map((u) => <option key={u.id} value={u.id}>{u.block}-{u.number} · {u.type}</option>)}
              </select>
            </Field>
          </div>
          {unit && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2" data-testid="bf-unit-autofetch">
              <RO label="Nama Perumahan" value={projects.find((p) => p.id === f.location_id)?.name || "-"} testid="bf-ro-perumahan" />
              <RO label="Blok / Nomor Unit" value={`${unit.block}-${unit.number}`} testid="bf-ro-blokunit" />
              <RO label="Luas Bangunan" value={`${unit.luas_bangunan} m²`} testid="bf-ro-luas" />
              <RO label="Alamat Rumah" value={`Blok ${unit.block}-${unit.number}, ${projects.find((p) => p.id === f.location_id)?.address || ""}`} testid="bf-ro-alamat" />
              <RO label="Harga Jual Rumah" value={fmtIDR(harga)} terbilangVal={harga} testid="bf-ro-harga" />
              <Field label="Metode Bayar">
                <select className={inputCls} value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)} data-testid="bf-method-select">
                  <option value="kpr">KPR</option><option value="cash">Cash Keras</option><option value="cash_bertahap">Cash Bertahap (12x)</option>
                </select>
              </Field>
            </div>
          )}
          {unit && addons.length > 0 && (
            <div className="mt-3" data-testid="bf-addons-section">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">Add-on (opsional — menambah harga jual)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {addons.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer hover:border-emerald-500/40 transition-colors" data-testid={`bf-addon-${a.id}`}>
                    <input type="checkbox" className="accent-emerald-500" checked={f.addon_ids.includes(a.id)} onChange={(e) => set("addon_ids", e.target.checked ? [...f.addon_ids, a.id] : f.addon_ids.filter((x) => x !== a.id))} />
                    <span className="text-xs text-slate-300 flex-1">{a.name}</span>
                    <span className="text-xs font-mono text-emerald-400">{fmtIDR(a.price)}</span>
                  </label>
                ))}
              </div>
              {addonTotal > 0 && <div className="text-[11px] font-mono text-emerald-300 mt-1.5">Total add-on: {fmtIDR(addonTotal)}</div>}
            </div>
          )}
          {unit && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <Field label="Diskon Booking (Rp)">
                <input type="number" min="0" className={inputCls} value={f.discount || ""} onChange={(e) => set("discount", +e.target.value)} data-testid="bf-diskon" />
                <Terbilang value={f.discount} testid="bf-diskon-terbilang" />
              </Field>
              <RO label="Harga Setelah Diskon + Add-on" value={fmtIDR(hargaEff)} terbilangVal={hargaEff} testid="bf-ro-harga-diskon" />
              <Field label="Besaran Uang Muka / DP (Rp)">
                <input type="number" className={inputCls} value={f.besaran_dp || ""} onChange={(e) => set("besaran_dp", +e.target.value)} data-testid="bf-dp" />
                <Terbilang value={f.besaran_dp} testid="bf-dp-terbilang" />
              </Field>
              <Field label="Promo DP (Rp) — memotong setoran DP konsumen">                <input type="number" min="0" className={inputCls} value={f.promo_dp || ""} onChange={(e) => set("promo_dp", +e.target.value)} data-testid="bf-promo-dp" />
                <Terbilang value={f.promo_dp} testid="bf-promo-dp-terbilang" />
                {f.promo_dp > 0 && <div className="text-[10px] text-amber-300 mt-1" data-testid="bf-promo-note">DP pada berkas bank tetap {fmtIDR(f.besaran_dp)} — konsumen cukup menyetor {fmtIDR(Math.max(f.besaran_dp - (f.promo_dp || 0), 0))}</div>}
              </Field>
              <Field label="Uang DP Yang Dibayar (Rp)">
                <input type="number" className={inputCls} value={f.dp_dibayar || ""} onChange={(e) => set("dp_dibayar", +e.target.value)} data-testid="bf-dp-dibayar" />
                <Terbilang value={f.dp_dibayar} testid="bf-dp-dibayar-terbilang" />
              </Field>
              <div className="sm:col-span-2 flex flex-wrap gap-4" data-testid="bf-gratis-flags">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 accent-emerald-500" checked={!!f.booking_fee_gratis} onChange={(e) => set("booking_fee_gratis", e.target.checked)} data-testid="bf-gratis-bf" />
                  <span className="text-xs text-slate-300">Booking Fee digratiskan <span className="text-slate-500">(tetap tertulis di berkas bank, konsumen tidak menyetor)</span></span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 accent-emerald-500" checked={!!f.dp_gratis} onChange={(e) => set("dp_gratis", e.target.checked)} data-testid="bf-gratis-dp" />
                  <span className="text-xs text-slate-300">Kekurangan DP digratiskan <span className="text-slate-500">(DP di berkas bank tetap penuh)</span></span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 accent-emerald-500" checked={!!f.menikah} onChange={(e) => set("menikah", e.target.checked)} data-testid="bf-menikah" />
                  <span className="text-xs text-slate-300">Sudah menikah <span className="text-slate-500">(KTP pasangan wajib di berkas)</span></span>
                </label>
              </div>
              <RO label="Kekurangan DP" value={fmtIDR(kekurangan)} terbilangVal={kekurangan} testid="bf-ro-kekurangan" />
              {f.payment_method === "kpr" && (
                <Field label="Nilai KPR (Rp) — otomatis, dapat diedit manual">
                  <input type="number" min="0" className={inputCls} value={kprManual ?? (nilaiKpr || "")} onChange={(e) => setKprManual(e.target.value === "" ? null : +e.target.value)} data-testid="bf-nilai-kpr" />
                  <Terbilang value={nilaiKprFinal} testid="bf-nilai-kpr-terbilang" />
                </Field>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-2">D. Administratif & Pengesahan</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Cabang Bank *">
              <select className={inputCls} value={f.cabang_bank} onChange={(e) => set("cabang_bank", e.target.value)} data-testid="bf-cabang-bank">
                <option value="">Pilih cabang...</option>
                {branches.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="TTD Cabang Bank (pihak bank) *">
              <select className={inputCls} value={f.ttd_cabang_bank} onChange={(e) => set("ttd_cabang_bank", e.target.value)} data-testid="bf-ttd-cabang">
                <option value="">Pilih cabang penandatangan...</option>
                {branches.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Tempat Tanda Tangan *"><input className={inputCls} placeholder="Stabat" value={f.tempat_tanda_tangan} onChange={(e) => set("tempat_tanda_tangan", e.target.value)} data-testid="bf-tempat-ttd" /></Field>
            <div className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2" data-testid="bf-tanggal">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1"><Lock className="w-2.5 h-2.5" />Tanggal (otomatis)</div>
              <div className="text-sm text-slate-200 mt-0.5 font-mono">{today()}</div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-800">
          <Badge tone={ready ? "emerald" : "rose"}>{ready ? "Formulir lengkap" : "Lengkapi kolom wajib (*)"}</Badge>
          <div className="flex gap-2">
            <Btn tone="ghost" onClick={onClose}>Batal</Btn>
            <Btn onClick={submit} disabled={!ready || submitting} data-testid="bf-submit">
              <FileSignature className="w-4 h-4" /> {submitting ? "Menyimpan..." : "Submit & Booking Unit"}
            </Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}
